import { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Boxes, Palette, Ruler, Scale, Package, Calendar, Globe, Layers,
  Users, Gift, Hash, FileText, Download, ShieldCheck,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { IDS } from '@/constants/testIds';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const sanitize = (html) =>
  DOMPurify.sanitize(String(html || ''), { USE_PROFILES: { html: true } });

function titleCase(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toParagraphs(text) {
  const s = String(text || '').trim();
  if (!s) return [];
  return s.split(/\n{2,}/).map((chunk) => chunk.replace(/\n/g, ' \n'));
}

const DOC_TYPE_LABEL = {
  declaration_of_conformity: 'Declaration of Conformity',
  sell_sheet: 'Sell Sheet',
  planogram: 'Planogram',
  instructions: 'Instructions',
  symbol_pvc: 'PVC Symbol',
  symbol_phthalate_free: 'Phthalate-Free Symbol',
};
const humanizeDocType = (t) => DOC_TYPE_LABEL[t] || titleCase(t);

// Format helper for spec values that may arrive as arrays or stringified lists.
function formatValue(v, unitSuffix = '') {
  if (v == null || v === '') return '';
  const raw = Array.isArray(v)
    ? v.join(', ')
    : (/^\s*\[.*\]\s*$/.test(String(v))
        ? String(v).replace(/^\s*\[|\]\s*$/g, '').replace(/['"]/g, '').trim()
        : String(v));
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return unitSuffix ? `${trimmed}${unitSuffix}` : trimmed;
}

// ---------------------------------------------------------------------------
// Description tab
// ---------------------------------------------------------------------------
function DescriptionTab({ description, featureBullets, featureBulletsHtml }) {
  const paragraphs = toParagraphs(description);
  const hasBullets = Array.isArray(featureBullets) && featureBullets.length > 0;
  const hasHtml = !hasBullets && !!featureBulletsHtml;

  return (
    <div className="space-y-8">
      {paragraphs.length > 0 && (
        <div className="serif text-zinc-800 text-base leading-relaxed space-y-4">
          {paragraphs.map((p, i) => (
            <p key={i} style={{ whiteSpace: 'pre-line' }}>{p}</p>
          ))}
        </div>
      )}

      {hasBullets && (
        <div>
          <p className="text-xs font-bold tracking-wider text-zinc-900 uppercase mb-3">Features</p>
          <ul className="space-y-2">
            {featureBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
                <span
                  className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: 'var(--ije-pink-dark, #C2478A)' }}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasHtml && (
        <div>
          <p className="text-xs font-bold tracking-wider text-zinc-900 uppercase mb-3">Features</p>
          <div
            className="prose prose-sm max-w-none text-zinc-700 leading-relaxed [&_li]:marker:text-[color:var(--ije-pink-dark,#C2478A)]"
            dangerouslySetInnerHTML={{ __html: sanitize(featureBulletsHtml) }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Specifications tab
// ---------------------------------------------------------------------------
function SpecRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-zinc-100 last:border-0">
      <Icon size={14} strokeWidth={1.5} className="mt-0.5 text-zinc-400 flex-shrink-0" />
      <div className="flex-1 flex items-start justify-between gap-4">
        <span className="text-sm text-zinc-500 w-40 flex-shrink-0">{label}</span>
        <span className="text-sm text-zinc-800 font-medium text-right">{value}</span>
      </div>
    </div>
  );
}

function SpecificationsTab({ specifications }) {
  const s = specifications || {};
  const left = [
    { icon: Layers, label: 'Material', value: formatValue(s.material) },
    { icon: Palette, label: 'Color', value: formatValue(s.color) },
    { icon: Boxes, label: 'Sub Type', value: formatValue(s.sub_product_type) },
    { icon: Gift, label: 'Includes', value: formatValue(s.comes_with) },
    { icon: Users, label: 'Target Gender', value: formatValue(s.target_gender) },
    { icon: Package, label: 'Packaging', value: formatValue(s.packaging_type) },
    { icon: Calendar, label: 'Release Date', value: formatValue(s.release_date) },
    { icon: Globe, label: 'Country of Origin', value: formatValue(s.country_of_origin) },
  ];
  const right = [
    { icon: Ruler, label: 'Package Size', value: formatValue(s.packaged_dimensions, ' in') },
    { icon: Scale, label: 'Package Weight', value: formatValue(s.packaged_weight_lbs, ' lbs') },
    { icon: Ruler, label: 'Case Dimensions', value: formatValue(s.case_dimensions, ' in') },
    { icon: Scale, label: 'Case Weight', value: formatValue(s.case_weight_lbs, ' lbs') },
    { icon: Boxes, label: 'Case Count', value: formatValue(s.case_count) },
    { icon: Hash, label: 'EAN', value: formatValue(s.ean) },
    { icon: Hash, label: 'HTS Code', value: formatValue(s.export_code) },
    { icon: FileText, label: 'HTS Description', value: formatValue(s.export_code_description) },
  ];

  return (
    <div className="space-y-8">
      <div className="grid gap-x-10 gap-y-1 md:grid-cols-2">
        <div className="divide-y divide-zinc-100">
          {left.map((row) => <SpecRow key={row.label} {...row} />)}
        </div>
        <div className="divide-y divide-zinc-100">
          {right.map((row) => <SpecRow key={row.label} {...row} />)}
        </div>
      </div>

      {s.product_dimensions_html && (
        <div>
          <p className="text-xs font-bold tracking-wider text-zinc-900 uppercase mb-3">
            Product Dimensions
          </p>
          <div
            className="prose prose-sm max-w-none text-zinc-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: sanitize(s.product_dimensions_html) }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Additional info tab
// ---------------------------------------------------------------------------
function AdditionalInfoTab({ additionalInfo }) {
  const entries = Object.entries(additionalInfo || {}).filter(
    ([, v]) => v != null && String(v).trim() !== ''
  );
  return (
    <div className="space-y-6">
      {entries.map(([key, value]) => (
        <div key={key}>
          <p className="text-xs font-bold tracking-wider text-zinc-900 uppercase mb-2">
            {titleCase(key)}
          </p>
          <p
            className="text-sm text-zinc-700 leading-relaxed"
            style={{ whiteSpace: 'pre-line' }}
          >
            {String(value)}
          </p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents tab
// ---------------------------------------------------------------------------
function DocumentRow({ doc }) {
  const url = doc.url;
  if (!url) return null;
  const label = doc.name || humanizeDocType(doc.type);
  const format = (doc.format || (url.split('.').pop() || '')).toUpperCase().slice(0, 6);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download
      className="group flex items-center gap-4 px-4 py-3 hover:bg-zinc-50 transition-colors"
    >
      <FileText
        size={18}
        strokeWidth={1.5}
        className="text-zinc-400 group-hover:text-red-600 transition-colors flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-900 font-medium truncate">{label}</p>
        {doc.type && doc.name && (
          <p className="text-xs text-zinc-500 truncate">{humanizeDocType(doc.type)}</p>
        )}
      </div>
      {format && (
        <span className="text-[10px] font-bold tracking-wider text-zinc-500 border border-zinc-200 px-2 py-0.5 uppercase">
          {format}
        </span>
      )}
      <Download
        size={16}
        strokeWidth={1.5}
        className="text-zinc-400 group-hover:text-red-600 transition-colors flex-shrink-0"
      />
    </a>
  );
}

function DocumentsTab({ documents, warrantyUrl }) {
  const validDocs = (documents || []).filter((d) => d && d.url);
  return (
    <div className="space-y-6">
      {validDocs.length > 0 && (
        <div className="border border-zinc-200 divide-y divide-zinc-100">
          {validDocs.map((doc, i) => <DocumentRow key={i} doc={doc} />)}
        </div>
      )}
      {warrantyUrl && (
        <a
          href={warrantyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-zinc-700 hover:text-red-600 transition-colors border-b border-zinc-200 hover:border-red-600 pb-0.5"
          data-testid="product-warranty-link"
        >
          <ShieldCheck size={16} strokeWidth={1.5} />
          Warranty Information
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ProductInfoTabs({ product }) {
  const description = product?.description || '';
  const featureBullets = product?.feature_bullets || [];
  const featureBulletsHtml = product?.feature_bullets_html || '';
  const specifications = product?.specifications || {};
  const additionalInfo = product?.additional_info || {};
  const documents = product?.documents || [];
  const warrantyUrl = product?.warranty_url || '';

  const tabs = useMemo(() => {
    const list = [];
    if (description || featureBullets.length > 0 || featureBulletsHtml) {
      list.push({ key: 'description', label: 'Description', testId: IDS.product.tabDescription });
    }
    if (Object.keys(specifications).length > 0) {
      list.push({
        key: 'specifications',
        label: 'Specifications',
        testId: IDS.product.tabSpecifications,
      });
    }
    if (Object.keys(additionalInfo).length > 0) {
      list.push({
        key: 'additional_info',
        label: 'Additional Info',
        testId: IDS.product.tabAdditionalInfo,
      });
    }
    const docCount = documents.filter((d) => d && d.url).length;
    if (docCount > 0 || warrantyUrl) {
      list.push({
        key: 'documents',
        label: `Documents${docCount ? ` (${docCount})` : ''}`,
        testId: IDS.product.tabDocuments,
      });
    }
    return list;
  }, [description, featureBullets, featureBulletsHtml, specifications, additionalInfo, documents, warrantyUrl]);

  const [active, setActive] = useState(tabs[0]?.key);

  if (!tabs.length) return null;
  const currentActive = tabs.find((t) => t.key === active) ? active : tabs[0].key;

  return (
    <div className="mt-10 md:mt-14" data-testid={IDS.product.tabs}>
      <Tabs value={currentActive} onValueChange={setActive} className="w-full">
        <div className="border-b border-zinc-200 -mx-4 px-4 overflow-x-auto">
          <TabsList className="h-auto rounded-none bg-transparent p-0 gap-1 justify-start">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={t.testId}
                className="rounded-none px-5 py-3 sans text-xs tracking-widest uppercase text-zinc-600 data-[state=active]:bg-zinc-900 data-[state=active]:text-white data-[state=active]:shadow-none transition-colors whitespace-nowrap"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {tabs.some((t) => t.key === 'description') && (
          <TabsContent value="description" className="pt-8">
            <DescriptionTab
              description={description}
              featureBullets={featureBullets}
              featureBulletsHtml={featureBulletsHtml}
            />
          </TabsContent>
        )}
        {tabs.some((t) => t.key === 'specifications') && (
          <TabsContent value="specifications" className="pt-8">
            <SpecificationsTab specifications={specifications} />
          </TabsContent>
        )}
        {tabs.some((t) => t.key === 'additional_info') && (
          <TabsContent value="additional_info" className="pt-8">
            <AdditionalInfoTab additionalInfo={additionalInfo} />
          </TabsContent>
        )}
        {tabs.some((t) => t.key === 'documents') && (
          <TabsContent value="documents" className="pt-8">
            <DocumentsTab documents={documents} warrantyUrl={warrantyUrl} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
